/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CART PAGE — VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * LOGIQUE:
 *   1. Lire infoSource.allSettings.pages.CART → pageSettings { sections, facets }
 *   2. Résolution — CASCADE STRICTE à 3 niveaux :
 *
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │  NIVEAU 1 — pageSettings.sections[]                             │
 *      │    sections[] non vide → extraire sectionIds triés + props.     │
 *      │    PRIORITAIRE. Si valide → STOP.                               │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 2 — pageSettings.facets[]                               │
 *      │    facets[] non vide → utiliser comme liste de sectionIds.      │
 *      │    Utilisé UNIQUEMENT si sections[] est vide/null.              │
 *      │    Si valide → STOP.                                            │
 *      ├─────────────────────────────────────────────────────────────────┤
 *      │  NIVEAU 3 — PAGES_CONFIG_.CART (defaults pagesConfig.js)        │
 *      │    Utilisé UNIQUEMENT si sections[] ET facets[] sont vides.     │
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *   CORE SECTIONS (toujours présentes) :
 *     cartEmpty | cartFullLayout
 *
 *   OPTIONAL (présentes par défaut, retirables) :
 *     newProducts | bestSellers | dealTodays
 *
 *   EXTRA (ajoutables depuis AnimationSettings) :
 *     userView, followShopUser, historySearchMatrix, ...
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../Layout/Layout";
import { getAddressUserIfTrue, isAuthenticated } from "../auth";
import { taxProductAreaByState } from "../auth/apiProduct";
import { laguangeElakay } from "../pageInside/dataElakay";
import DynamicPageRenderer from "../pageInside/exeemple_page/DynamicPageRenderer";
import useDataLoader from "../pageInside/exeemple_page/DataLoader";
import { getInfoSourceUpdates } from "../pageInside/exeemple_page/allFacets";
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from "../pageInside/exeemple_page/pagesConfig";

// ═══════════════════════════════════════════════════════════════════════════
// CART STYLES
// ═══════════════════════════════════════════════════════════════════════════

const cartStyles = `
.cart-page-container {
  position: relative;
  padding: 1.5rem 1rem;
  margin: 0 auto;
  max-width: 100%;
  border-radius: 18px;
  background:
    radial-gradient(circle at 0% 0%, rgba(0, 164, 216, 0.06) 0%, transparent 55%),
    radial-gradient(circle at 100% 100%, rgba(0, 115, 153, 0.06) 0%, transparent 55%),
    linear-gradient(135deg, #ffffff 0%, #f8fafc 40%, #f1f5f9 100%);
  box-shadow:
    0 18px 40px rgba(15, 23, 42, 0.08),
    0 0 0 1px rgba(148, 163, 184, 0.18);
  overflow: hidden;
  animation: cartEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1) both;
}
@keyframes cartEnter {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
.cart-page-container::before {
  content: '';
  position: absolute;
  inset: -40%;
  background:
    radial-gradient(circle at 15% 20%, rgba(0, 164, 216, 0.08) 0%, transparent 55%),
    radial-gradient(circle at 85% 80%, rgba(0, 115, 153, 0.10) 0%, transparent 55%);
  opacity: 0.7;
  z-index: 0;
  pointer-events: none;
}
.cart-page-container > * { position: relative; z-index: 1; }
.cart-header { margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 2px solid #e2e8f0; }
.cart-title {
  font-size: 1.75rem; font-weight: 700; color: #1e293b; margin: 12px;
  display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
  max-width: 100%; overflow-wrap: anywhere;
}
.cart-badge {
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  color: #ffffff; padding: 0.35rem 0.85rem; border-radius: 12px;
  font-size: 0.9375rem; font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 115, 153, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.cart-badge:hover { transform: scale(1.05); box-shadow: 0 4px 10px rgba(0, 115, 153, 0.3); }
.cart-items-column { display: flex; flex-direction: column; gap: 1rem; }
.cart-checkout-column { position: sticky; top: 1rem; align-self: flex-start; }
@media (hover: hover) and (pointer: fine) {
  .cart-page-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 50px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.24);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
@media (min-width: 992px) { .cart-page-container { padding: 2rem 1.5rem; } .cart-title { font-size: 2rem; } }
@media (min-width: 768px) and (max-width: 991px) { .cart-page-container { padding: 1.5rem 1rem; } .cart-checkout-column { position: static; margin-top: 2rem; } }
@media (max-width: 767px) {
  .cart-page-container { padding: 1rem 0.75rem; border-radius: 12px; }
  .cart-header { margin-bottom: 1rem; } .cart-title { font-size: 1.5rem; }
  .cart-badge { font-size: 0.875rem; padding: 0.25rem 0.65rem; }
  .cart-checkout-column { position: static; margin-top: 1.5rem; } .cart-items-column { gap: 0.75rem; }
}
@media (max-width: 575px) { .cart-page-container { padding: 0.75rem 0.5rem; border-radius: 10px; } .cart-title { font-size: 1.375rem; } }
@media (prefers-reduced-motion: reduce) { .cart-page-container, .cart-badge { animation: none !important; transition: none !important; } }
`;

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'CART';

/**
 * CORE_SECTIONS — toujours présentes, toujours enabled.
 * cartEmpty    → s'affiche si panier vide   (dataCondition dans SECTIONS_LIBRARY)
 * cartFullLayout → s'affiche si panier plein (dataCondition dans SECTIONS_LIBRARY)
 */
const CORE_SECTIONS = ['cartEmpty', 'cartFullLayout'];

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.CART
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return ['cartEmpty', 'cartFullLayout', 'newProducts', 'bestSellers', 'dealTodays'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) {
            result[s.sectionId] = entry;
        }
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {

        const mergedMap = new Map();

        sections.forEach(s => {
            if (!s.sectionId) return;
            mergedMap.set(s.sectionId, {
                sectionId: s.sectionId,
                order:     s.order    ?? 0,
                enabled:   CORE_SECTIONS.includes(s.sectionId)
                               ? true
                               : s.enabled !== false,
                props:     s.props    || {},
                overrides: s.overrides || {},
            });
        });

        // Garantir la présence des CORE absents
        CORE_SECTIONS.forEach((coreId, idx) => {
            if (!mergedMap.has(coreId)) {
                mergedMap.set(coreId, {
                    sectionId: coreId,
                    order:     -(CORE_SECTIONS.length - idx),
                    enabled:   true,
                    props:     {},
                    overrides: {},
                });
            }
        });

        const sorted = [...mergedMap.values()]
            .sort((a, b) => a.order - b.order);

        const resolvedFacets = sorted
            .filter(s => s.enabled)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        const base = [...facets];
        CORE_SECTIONS.forEach(coreId => {
            if (!base.includes(coreId)) base.unshift(coreId);
        });
        return { facets: base, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE STRICTE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const Cart = ({ infoSource }) => {
    const { cartItems, getCartItems } = infoSource;
    const { token } = isAuthenticated();

    // ────────────────────────────────────────────────────────────────────────
    // 1. CART STATE
    // ────────────────────────────────────────────────────────────────────────

    const [ShipAmount,      SetShipAmount]      = useState([]);
    const [Saddress,        setSAddressFinal]   = useState({
        firstName: "", lastName: "", city: "", country: "",
        line1: "", postal_code: "", state: "",
    });

    // ────────────────────────────────────────────────────────────────────────
    // 2. CASCADE — résoudre config depuis AnimationSettings
    // ────────────────────────────────────────────────────────────────────────

    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides: baseConfigOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [
            JSON.stringify(pageSettings?.sections),
            JSON.stringify(pageSettings?.facets),
        ]
    );

    // ────────────────────────────────────────────────────────────────────────
    // 3. DATA LOADER
    //    useDataLoader gère automatiquement:
    //      NONE     → cartEmpty, cartFullLayout (sync depuis infoSource)
    //      PRODUCTS → newProducts, bestSellers, dealTodays, ...
    // ────────────────────────────────────────────────────────────────────────

    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        limit: 15,
        skip:  0,
    });

    // ────────────────────────────────────────────────────────────────────────
    // 4. SYNC infoSource → pageData (sections loaderType NONE)
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, facets]);

    // ────────────────────────────────────────────────────────────────────────
    // 5. SYNC cartItems → pageData
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (getCartItems) {
            mergePageData({ cartItems: getCartItems });
        }
    }, [getCartItems]);

    // ────────────────────────────────────────────────────────────────────────
    // 6. CHARGEMENT initial + reload si facets changent
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        loadPageData();
    }, [JSON.stringify(facets)]);

    // ────────────────────────────────────────────────────────────────────────
    // 7. BUSINESS LOGIC — address + tax
    // ────────────────────────────────────────────────────────────────────────

    const addressId = useCallback((tok) => {
        if (!tok) return;
        getAddressUserIfTrue(tok).then(data => {
            if (data && !data.error && data[0]) {
                setSAddressFinal({
                    firstName:   data[0].firstName,
                    lastName:    data[0].lastName,
                    tel:         data[0].tel,
                    city:        data[0].city,
                    country:     data[0].country,
                    line1:       data[0].Address1,
                    postal_code: data[0].postalCode,
                    state:       data[0].State,
                });
            }
        });
    }, []);

    const newCartProd = useCallback(async (newCart) => {
        if (!newCart) return;
        for (let i = 0; i < newCart.length; i++) {
            if (newCart[i].addressShop) {
                const additionalData = await taxProductAreaByState(
                    newCart[i].addressShop.country,
                    newCart[i].addressShop.postalCode
                );
                if (additionalData) {
                    newCart[i].taxprodZone = additionalData.zoneTax;
                }
            }
        }
    }, []);

    // Init address + tax
    useEffect(() => {
        addressId(token);
        newCartProd(getCartItems);
    }, []);

    // Reload address + tax quand token/cart change
    useEffect(() => {
        addressId(token);
        newCartProd(getCartItems);
    }, [token, getCartItems]);

    // ────────────────────────────────────────────────────────────────────────
    // 8. configOverrides finaux
    //    Merge: baseConfigOverrides + callbacks React (non-sérialisables)
    // ────────────────────────────────────────────────────────────────────────

    const configOverrides = useMemo(() => ({
        ...baseConfigOverrides,
        cartFullLayout: {
            ...baseConfigOverrides.cartFullLayout,
            props: {
                ...(baseConfigOverrides.cartFullLayout?.props || {}),
                Saddress,
                setSAddressFinal,
                SetShipAmount,
                cartItems,
                cartStyles,
            },
        },
    }), [baseConfigOverrides, Saddress, cartItems]);

    // ────────────────────────────────────────────────────────────────────────
    // 9. STYLES
    // ────────────────────────────────────────────────────────────────────────

    const homeStyles = useMemo(() => ({
        width:           '100%',
        backgroundColor: '#f0f0f0',
    }), []);

    // ────────────────────────────────────────────────────────────────────────
    // 10. RENDER
    // ────────────────────────────────────────────────────────────────────────

    return (
        <Layout
            infoSource={infoSource}
            title={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).cart.t001}
            className="container-fluid"
            SliderPunShow={false}
            loading={loading}
            {...infoSource?.allSettings?.layout}
        >
            <style>{cartStyles}</style>
            <div>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    reloadFacets={reloadFacets}
                    configOverrides={configOverrides}
                />
            </div>
        </Layout>
    );
};

export default Cart;
